import React, { useState, useCallback } from 'react';
import { Upload, Button, Image, Modal, Progress, message, Space, Typography, Card, Row, Col, Tag, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined, UploadOutlined, InboxOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Dragger } = Upload;
const { Text, Title } = Typography;

function ImageUpload({
    value = [],
    onChange,
    maxCount = 1,
    accept = 'image/*',
    maxSize = 10, // MB
    category = 'general',
    showPreview = true,
    showList = true,
    disabled = false,
    uploadText = 'Click or drag file to this area to upload',
    uploadHint = 'Support for single or bulk upload. Strictly prohibit from uploading company data or other band files',
    className = '',
    style = {},
    onUploadSuccess,
    onUploadError,
    onRemove,
    previewWidth = 200,
    previewHeight = 200,
    showDimensions = true,
    showFileSize = true,
    showAltText = true,
    allowCrop = false,
    cropAspectRatio = null,
    quality = 0.8
}) {
    const [uploading, setUploading] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleUpload = useCallback(async (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return false;
        }

        const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
        if (!isLtMaxSize) {
            message.error(`Image must be smaller than ${maxSize}MB!`);
            return false;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', category);
            formData.append('alt_text', file.name);
            formData.append('is_public', '1');

            // Use vendor upload endpoint for vendor users, admin endpoint for admin users
            const authUser = localStorage.getItem('auth_user');
            let userRole = null;
            
            if (authUser) {
                try {
                    const userData = JSON.parse(authUser);
                    userRole = userData.user_type;
                } catch (e) {
                    console.error('Failed to parse auth user data:', e);
                }
            }
            
            let response;
            if (userRole === 'vendor') {
                response = await api.uploadVendorMedia(file, category, { alt_text: file.name });
            } else {
                response = await api.adminUploadMedia(formData);
            }
            
            if (response.success) {
                const variants = response.data.variants || null;
                const originalUrl = (response.data && (response.data.url || response.data.file_path)) ? (response.data.url || response.data.file_path) : '';
                const previewUrl = (variants && (variants.thumb || variants.medium)) || originalUrl;
                const newImage = {
                    id: response.data.id,
                    url: previewUrl,
                    original_url: originalUrl,
                    variants: variants,
                    name: response.data.original_name,
                    size: response.data.file_size,
                    type: response.data.file_type,
                    dimensions: response.data.dimensions,
                    alt_text: response.data.alt_text,
                    category: response.data.category,
                    uploaded_at: response.data.created_at
                };

                const newValue = maxCount === 1 ? [newImage] : [...(value || []), newImage];
                onChange?.(newValue);
                onUploadSuccess?.(newImage);
                message.success('Image uploaded successfully!');
            } else {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error(error.message || 'Upload failed');
            onUploadError?.(error);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }

        return false; // Prevent default upload behavior
    }, [value, onChange, maxCount, category, maxSize, onUploadSuccess, onUploadError]);

    const handleRemove = useCallback(async (file) => {
        try {
            if (file.id) {
                await api.adminDeleteMedia(file.id);
            }
            
            const newValue = (value || []).filter(item => item.id !== file.id);
            onChange?.(newValue);
            onRemove?.(file);
            message.success('Image removed successfully!');
        } catch (error) {
            console.error('Remove error:', error);
            message.error('Failed to remove image');
        }
    }, [value, onChange, onRemove]);

    const handlePreview = useCallback((file) => {
        setPreviewImage(file.url);
        setPreviewVisible(true);
        setPreviewTitle(file.name || file.alt_text || 'Preview');
    }, []);

    const uploadProps = {
        name: 'file',
        multiple: maxCount > 1,
        accept: accept,
        beforeUpload: handleUpload,
        showUploadList: false,
        disabled: disabled || uploading,
        customRequest: () => {}, // Prevent default upload
    };

    const renderUploadButton = () => {
        if (maxCount === 1 && value && value.length > 0) {
            return null; // Hide upload button when single image is uploaded
        }

        if (maxCount > 1 && value && value.length >= maxCount) {
            return null; // Hide upload button when max count reached
        }

        return (
            <div className="text-center">
                <Upload {...uploadProps}>
                    <Button 
                        type="dashed" 
                        icon={<PlusOutlined />}
                        loading={uploading}
                        disabled={disabled}
                        className="w-full"
                    >
                        {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                </Upload>
            </div>
        );
    };

    const renderImageList = () => {
        if (!value || value.length === 0) return null;

        return (
            <div className="space-y-4">
                {value.map((file, index) => (
                    <Card 
                        key={file.id || index}
                        size="small"
                        className="relative group"
                        hoverable
                    >
                        <Row gutter={[16, 16]} align="middle">
                            <Col flex="0 0 auto">
                                <Image
                                    src={file.url}
                                    alt={file.alt_text || file.name}
                                    width={previewWidth}
                                    height={previewHeight}
                                    className="rounded object-cover"
                                    fallback="/images/placeholder.svg"
                                />
                            </Col>
                            <Col flex="1">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Text strong className="truncate">
                                            {file.name || file.alt_text || 'Image'}
                                        </Text>
                                        <Space>
                                            {showPreview && (
                                                <Tooltip title="Preview">
                                                    <Button 
                                                        type="text" 
                                                        icon={<EyeOutlined />}
                                                        size="small"
                                                        onClick={() => handlePreview(file)}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Remove">
                                                <Button 
                                                    type="text" 
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    size="small"
                                                    onClick={() => handleRemove(file)}
                                                />
                                            </Tooltip>
                                        </Space>
                                    </div>
                                    
                                    <div className="space-y-1">
                                        <Tag color="blue">{file.category}</Tag>
                                        <Tag color="green">{file.type}</Tag>
                                        {showFileSize && (
                                            <Tag color="orange">
                                                {(file.size / 1024 / 1024).toFixed(2)} MB
                                            </Tag>
                                        )}
                                        {showDimensions && file.dimensions && (
                                            <Tag color="purple">
                                                {file.dimensions.width} × {file.dimensions.height}
                                            </Tag>
                                        )}
                                    </div>
                                    
                                    {showAltText && (
                                        <Text type="secondary" className="text-xs">
                                            Alt: {file.alt_text || 'No alt text'}
                                        </Text>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        
                        {uploading && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                                <div className="text-center">
                                    <Progress 
                                        percent={uploadProgress} 
                                        size="small" 
                                        status="active"
                                    />
                                    <Text className="text-xs">Uploading...</Text>
                                </div>
                            </div>
                        )}
                    </Card>
                ))}
            </div>
        );
    };

    return (
        <div className={`image-upload ${className}`} style={style}>
            {showList && renderImageList()}
            
            {renderUploadButton()}
            
            {uploading && (
                <div className="mt-4">
                    <Progress 
                        percent={uploadProgress} 
                        status="active"
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                    />
                </div>
            )}

            <Modal
                open={previewVisible}
                title={previewTitle}
                footer={null}
                onCancel={() => setPreviewVisible(false)}
                width={800}
            >
                <Image
                    alt={previewTitle}
                    style={{ width: '100%' }}
                    src={previewImage}
                />
            </Modal>
        </div>
    );
}

// Specialized components for different use cases
export const CategoryImageUpload = (props) => (
    <ImageUpload
        {...props}
        category="category"
        maxCount={1}
        previewWidth={120}
        previewHeight={120}
        showDimensions={false}
        uploadText="Upload Category Image"
        uploadHint="Upload an image for this category"
    />
);

export const MenuImageUpload = (props) => (
    <ImageUpload
        {...props}
        category="menu"
        maxCount={1}
        previewWidth={150}
        previewHeight={150}
        uploadText="Upload Menu Image"
        uploadHint="Upload an image for this menu item"
    />
);

export const ProfileImageUpload = (props) => (
    <ImageUpload
        {...props}
        category="profile"
        maxCount={1}
        previewWidth={100}
        previewHeight={100}
        showDimensions={false}
        uploadText="Upload Profile Picture"
        uploadHint="Upload a profile picture"
    />
);

export const BannerImageUpload = (props) => (
    <ImageUpload
        {...props}
        category="banner"
        maxCount={1}
        previewWidth={300}
        previewHeight={150}
        uploadText="Upload Banner Image"
        uploadHint="Upload a banner image for the homepage"
    />
);

export const HeroSliderUpload = (props) => (
    <ImageUpload
        {...props}
        category="hero-slider"
        maxCount={5}
        previewWidth={200}
        previewHeight={120}
        uploadText="Upload Hero Slider Images"
        uploadHint="Upload images for the hero slider (max 5)"
    />
);

export const GalleryUpload = (props) => (
    <ImageUpload
        {...props}
        category="gallery"
        maxCount={10}
        previewWidth={120}
        previewHeight={120}
        uploadText="Upload Gallery Images"
        uploadHint="Upload images for the gallery (max 10)"
    />
);

export default ImageUpload;
